import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { saveBookingTrackingProps } from '@/helpers/checkout';
import {
  buyBundleFlowTrackingSession,
  paymentActivationFlowTrackingSession,
} from '@/helpers/checkoutFlowTrackingSession';
import { trackGiftcardPurchase } from '@/helpers/giftcards';
import { _s } from '@/locale';
import { swishServices } from '@/services';
import { CheckoutTracking } from '@/types/analytics/checkout';
import { ErrorResponse } from '@/types/api/services/schema';
import { SwishPaymentResponse, keepAliveNotificationSchema, swishNotificationSchema } from '@/types/api/services/swish';
import { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProductType as GiftcardType } from '../presentkort/GiftcardCheckout.types';
import { baseTranslationKey } from './ValidateSwishPaymentRedirect';

export type SwishSuccessRedirectData = {
  paymentMethod: typeof CHECKOUT_PAYMENT_METHOD.SWISH;
  responseData: SwishPaymentResponse;
};

type ResourceType = 'booking' | 'bundle' | GiftcardType;

const SERVER_ERROR = { message: 'Could not get status of the swish payment', status: 500 };
const FAILED_PAYMENT_ERROR = {
  message: 'The swish payment was not successful',
  status: 400,
  clientError: _s(`${baseTranslationKey}.failedPaymentError`),
};

export const PAYMENT_REDIRECT_LOCALSTORAGE_KEY = 'payment-redirect-swish';

type SubmitSwishRedirectContext = {
  payment: SwishPaymentResponse;
  eventSource: EventSource;
  trackingProps: CheckoutTracking.Booking | CheckoutTracking.Bundle;
  successCallback: (props: SwishSuccessRedirectData) => void;
  errorCallback: (error: ErrorResponse) => void;
};

export type SavedSwishPaymentCheckoutState = {
  trackingProps: CheckoutTracking.Booking | CheckoutTracking.Bundle | CheckoutTracking.Giftcard;
  payment: SwishPaymentResponse;
  /**
   * If we are redirected from a valuecard checkout we need to save the value card in state
   * so that we can restore the state if the user is redirected back from swish redirect hook
   * on a failed payment. (we need to valuecard in location.state)
   */
  fallbackLocationState?: unknown;
};

function getSavedCheckoutStateFromLocalStorage(): SavedSwishPaymentCheckoutState | null {
  const savedCheckoutState: SavedSwishPaymentCheckoutState = JSON.parse(
    localStorage.getItem(PAYMENT_REDIRECT_LOCALSTORAGE_KEY),
  );
  savedCheckoutState && localStorage.removeItem(PAYMENT_REDIRECT_LOCALSTORAGE_KEY);

  return savedCheckoutState;
}

export function saveSwishCheckoutStateToLocalStorage(state: SavedSwishPaymentCheckoutState) {
  localStorage.setItem(PAYMENT_REDIRECT_LOCALSTORAGE_KEY, JSON.stringify(state));
}

export function removeSwishCheckoutStateToLocalStorage() {
  localStorage.removeItem(PAYMENT_REDIRECT_LOCALSTORAGE_KEY);
}

/**
 * If we are redirected from swish we need to check if the payment was successful or not and redirect
 * the user accordingly. The actual booking has already been submitted and will be completed or cancelled
 * in the backend.
 */
async function handleValidateSwishPayment(context: SubmitSwishRedirectContext) {
  const { payment, trackingProps, eventSource } = context;
  if (eventSource) {
    eventSource.onmessage = (e) => {
      const swishNotification = JSON.parse(e.data);
      const validatedKeepAliveNotification = keepAliveNotificationSchema.safeParse(swishNotification);

      if (validatedKeepAliveNotification.success === true && validatedKeepAliveNotification.data.keepAlive) {
        return;
      }

      const validatedSwishNotification = swishNotificationSchema.safeParse(swishNotification);

      if (validatedSwishNotification.success === false) {
        context.errorCallback(
          swishNotification.clientError
            ? {
                clientError: swishNotification.clientError,
                status: 400,
                message: 'The swish payment was not successful',
              }
            : SERVER_ERROR,
        );
        eventSource.close();
        return;
      }

      const { success, id, clientError } = validatedSwishNotification.data;

      if (!success) {
        context.errorCallback(
          clientError
            ? {
                clientError: clientError,
                status: 400,
                message: 'The swish payment was not successful',
              }
            : FAILED_PAYMENT_ERROR,
        );
      } else {
        saveBookingTrackingProps(trackingProps);
        context.successCallback({
          paymentMethod: CHECKOUT_PAYMENT_METHOD.SWISH,
          responseData: { ...payment, id },
        });
      }

      eventSource.close();
    };

    eventSource.onerror = () => {
      context.errorCallback(SERVER_ERROR);
      eventSource.close();
    };
  }
}

const getRedirectPathname = (
  type: ResourceType,
  id: number,
  /**
   * placeId is either a placeId or a place slug
   */
  placeId?: number | string,
): { successPathname: string; failurePathname: string } => {
  switch (type) {
    case 'booking':
      return {
        successPathname: '/booking/confirmation',
        failurePathname: `/booking/checkout/${id}`,
      };
    case 'bundle':
      return {
        successPathname: '/bundles/confirmation',
        failurePathname: `/bundles/checkout/${placeId}/${id}`,
      };
    case 'giftcard':
      return {
        successPathname: `/giftcard/confirmation/${id}`,
        failurePathname: `/presentkort`,
      };
    case 'wellnesscard':
      return {
        successPathname: `/giftcard/confirmation/${id}`,
        failurePathname: `/friskvardskort`,
      };
    case 'valuecard':
      return {
        successPathname: `/places/${placeId}/valuecard/confirmation/${id}`,
        failurePathname: `/places/${placeId}/valuecard/checkout`,
      };
    case 'placecard':
      return {
        successPathname: `/places/${placeId}/placecard/confirmation/${id}`,
        failurePathname: `/places/${placeId}/giftcard/checkout`,
      };
  }
};

const useValidateSwishRedirect = () => {
  const [checkoutState] = useState<SavedSwishPaymentCheckoutState | null>(getSavedCheckoutStateFromLocalStorage());
  const [redirect, setRedirect] = useState<any | false>(false);
  const [isNewBrowser, setIsNewBrowser] = useState<boolean>(false);
  const routeMatch = useRouteMatch() as { params: { id: string; type: ResourceType; placeId?: string } };

  const { id, type, placeId } = routeMatch.params;
  const { trackingProps, payment, fallbackLocationState } = checkoutState || {};
  const pathname = getRedirectPathname(type, Number(id), placeId);

  useEffect(() => {
    if (!payment) {
      setIsNewBrowser(true);
      return;
    }

    const successCallback = ({ responseData }: SwishSuccessRedirectData) => {
      if (['wellnesscard', 'giftcard', 'placecard', 'valuecard'].includes(type)) {
        if (checkoutState.trackingProps) {
          checkoutState.trackingProps['giftcard_id'] = responseData?.id;
        }
        trackGiftcardPurchase(checkoutState.trackingProps);
      }

      if (type === 'booking') {
        paymentActivationFlowTrackingSession.checkoutPaymentMethod('success', 'swish');
      }

      if (type === 'bundle') {
        buyBundleFlowTrackingSession.checkoutPaymentMethod('success', 'swish');
      }

      setRedirect({
        pathname: pathname.successPathname,
        state: { selectedPaymentMethod: { type: CHECKOUT_PAYMENT_METHOD.SWISH } },
        search: (() => {
          switch (type) {
            case 'booking':
              return `?bookingId=${responseData.id}&type=swishActivatePayment`;
            case 'bundle':
              return `?bundleId=${responseData.id}`;
          }
        })(),
      });
    };

    const errorCallback = ({ status, clientError }: { status: number; clientError?: string }) => {
      // This means we have dont know the status of the booking or payment
      if (status > 500) {
        toast(({ closeToast }) => (
          <Snackbar label={_s(`${baseTranslationKey}.unknownError`)} type="danger" onClose={closeToast} />
        ));
      } else {
        toast(({ closeToast }) => <Snackbar label={clientError} type="danger" onClose={closeToast} />);
      }

      setRedirect({
        pathname: pathname.failurePathname,
        search: '',
        state: fallbackLocationState,
      });
    };

    const context: SubmitSwishRedirectContext = {
      trackingProps,
      payment,
      errorCallback,
      successCallback,
      eventSource: swishServices.waitForSwishNotification(payment.channel),
    };

    handleValidateSwishPayment(context);

    return () => {
      context.eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { redirect, isNewBrowser };
};

export default useValidateSwishRedirect;
